/**
 * 공유 폴더(디렉토리) 서비스
 * 디렉토리 조회, 생성, 학습 기록 이동 기능 제공
 */

import apiClient from '../../core/http/axios';
import { LearningRecordData } from './learningRecordService';

// 디렉토리 데이터 타입
export interface SharedFolderData {
  folder_uuid: string;
  book_uuid: string;
  owner_no: number;
  title: string;
  description: string;
  meta: Record<string, any>;
  created_at: string;
  updated_at: string;
  content_updated_at: string;
  is_new: boolean;
  changed_at: string;
}

// API 응답 타입
interface SharedFolderListResponse {
  success: boolean;
  error: string | null;
  data: SharedFolderData[];
  code: string | null;
  meta?: {
    elapsed_ms: number;
  };
}

interface SharedFolderCreateRequest {
  book_uuid: string;
  title: string;
  description?: string;
  meta?: Record<string, any>;
}

interface SharedFolderCreateResponse {
  success: boolean;
  error: string | null;
  data: SharedFolderData;
  code: string | null;
  meta?: {
    elapsed_ms: number;
  };
}

// Annotation 데이터 타입 (API 응답의 annotation 객체)
export interface FolderItemAnnotation {
  annotation_uuid: string;
  book_no: number;
  page_no: number;
  page_idx: number;
  page_display_number: number;
  user_no: number;
  type: string; // 'HIGHLIGHT' | 'MEMO' | 'UNDERLINE' | 'BOX'
  component_no: number;
  start_offset: number;
  end_offset: number;
  data: {
    color?: string;
    title?: string;
    subject?: string;
    border_color?: string;
    [key: string]: unknown;
  };
  is_deleted: boolean;
  version: number;
  created_at: string;
  updated_at: string;
}

// 폴더 내부 아이템 타입
export interface FolderItem {
  item_no: number;
  item_type: string;
  ref_uuid: string;  // 현재 annotation_uuid => 향후 learning_record_uuid로 변경 필요
  sort_order: number;
  added_at: string;
  record: LearningRecordData | null; // 일부 아이템은 record가 null일 수 있음
  annotation: FolderItemAnnotation | null; // annotation 객체 (ANNOTATION 타입일 때 포함)
}

interface FolderItemsResponse {
  success: boolean;
  error: string | null;
  data: {
    total: number;
    limit: number;
    offset: number;
    items: FolderItem[];
  };
  code: string | null;
  meta?: {
    elapsed_ms: number;
  };
}

interface AddItemToFolderRequest {
  record_uuid: string;
  sort_order: number;
  payload: Record<string, any>;
}

interface AddItemToFolderResponse {
  success: boolean;
  error: string | null;
  data: any;
  code: string | null;
  meta?: {
    elapsed_ms: number;
  };
}

/** 폴더 아이템 추가 API 결과 (실패 시 error에 서버/HTTP 메시지) */
export interface AddItemToFolderResult {
  success: boolean;
  error: string | null;
}

function normalizeAxiosError(error: unknown): string {
  const ax = error as { response?: { data?: unknown } };
  const d = ax?.response?.data;
  if (typeof d === 'string') return d;
  if (d && typeof d === 'object') {
    const o = d as Record<string, unknown>;
    if (typeof o.error === 'string') return o.error;
    if ('detail' in o) {
      const detail = o.detail;
      if (typeof detail === 'string') return detail;
      if (Array.isArray(detail)) {
        return detail
          .map((x: unknown) =>
            x && typeof x === 'object' && 'msg' in (x as object)
              ? String((x as { msg?: string }).msg)
              : JSON.stringify(x)
          )
          .join(', ');
      }
    }
    if (typeof o.message === 'string') return o.message;
  }
  if (error instanceof Error) return error.message;
  return '요청에 실패했습니다.';
}

/** 이미 폴더에 동일 학습기록이 있는 경우 등 (서버 메시지 휴리스틱) */
export function isDuplicateSharedFolderItemError(message: string): boolean {
  if (!message) return false;
  const m = message.toLowerCase();
  if (m.includes('duplicate') || m.includes('unique') || m.includes('already exists')) return true;
  if (message.includes('이미') || message.includes('중복')) return true;
  return false;
}

interface DeleteItemResponse {
  success: boolean;
  error: string | null;
  data: {
    item_no?: number;
    folder_uuid?: string;
    deleted: boolean;
  };
  code: string | null;
  meta?: {
    elapsed_ms: number;
  };
}

interface DeleteFolderResponse {
  success: boolean;
  error: string | null;
  data: {
    folder_uuid: string;
    deleted: boolean;
  };
  code: string | null;
  meta?: {
    elapsed_ms: number;
  };
}

// 캐시 저장소
const sharedFoldersCache = new Map<string, { data: SharedFolderData[]; timestamp: number }>();
const folderItemsCache = new Map<string, { data: { items: FolderItem[]; total: number }; timestamp: number }>();
const CACHE_TTL = 60000; // 60초 캐시 유효 시간

/**
 * 공유 폴더 서비스
 */
export const sharedFolderService = {
  /**
   * 캐시 무효화 (외부에서 호출 가능)
   */
  invalidateCache: (bookUuid?: string, folderUuid?: string) => {
    if (bookUuid) {
      sharedFoldersCache.delete(bookUuid);
    }
    if (folderUuid) {
      // 해당 폴더의 모든 캐시 삭제 (limit/offset 상관없이)
      for (const key of folderItemsCache.keys()) {
        if (key.startsWith(`${folderUuid}:`)) {
          folderItemsCache.delete(key);
        }
      }
    }
    if (!bookUuid && !folderUuid) {
      // 전체 캐시 무효화
      sharedFoldersCache.clear();
      folderItemsCache.clear();
    }
  },

  /**
   * 디렉토리 목록 조회
   * @param bookUuid 책 UUID
   * @returns 디렉토리 목록
   */
  getSharedFolders: async (bookUuid: string): Promise<SharedFolderData[]> => {
    try {
      // 캐시 확인
      const cached = sharedFoldersCache.get(bookUuid);
      if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
        return cached.data;
      }

      const response = await apiClient.get<SharedFolderListResponse>(
        `/viewer/shared-folder`,
        {
          params: {
            book_uuid: bookUuid,
          },
        }
      );

      if (!response.data.success) {
        return [];
      }

      const data = response.data.data || [];
      // 캐시에 저장
      sharedFoldersCache.set(bookUuid, { data, timestamp: Date.now() });
      return data;
    } catch (error) {
      return [];
    }
  },

  /**
   * 디렉토리 생성
   * @param bookUuid 책 UUID
   * @param title 디렉토리 제목
   * @param description 디렉토리 설명 (선택)
   * @param meta 메타데이터 (선택)
   * @returns 생성된 디렉토리 데이터
   */
  createSharedFolder: async (
    bookUuid: string,
    title: string,
    description?: string,
    meta?: Record<string, any>
  ): Promise<SharedFolderData | null> => {
    try {
      // console.log('[SharedFolder] 디렉토리 생성 API 호출:', { bookUuid, title, description });
      
      const requestData: SharedFolderCreateRequest = {
        book_uuid: bookUuid,
        title,
        description: description || '',
        meta: meta || {},
      };

      const response = await apiClient.post<SharedFolderCreateResponse>(
        `/viewer/shared-folder`,
        requestData
      );

      // console.log('[SharedFolder] 디렉토리 생성 응답:', response.data);

      if (!response.data.success) {
        return null;
      }

      // 캐시 무효화
      sharedFoldersCache.delete(bookUuid);
      return response.data.data;
    } catch (error) {
      return null;
    }
  },

  /**
   * 폴더 내부 아이템 조회
   * @param folderUuid 폴더 UUID
   * @param limit 조회할 최대 개수 (기본값: 20)
   * @param offset 조회 시작 위치 (기본값: 0)
   * @returns 폴더 내부 아이템 목록과 메타데이터
   */
  getFolderItems: async (
    folderUuid: string,
    limit: number = 20,
    offset: number = 0
  ): Promise<{ items: FolderItem[]; total: number; limit: number; offset: number }> => {
    try {
      // 캐시 키 생성 (limit=1은 개수 확인용이므로 별도 캐시)
      const cacheKey = `${folderUuid}:${limit}:${offset}`;
      const cached = folderItemsCache.get(cacheKey);
      if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
        return {
          items: cached.data.items,
          total: cached.data.total,
          limit: limit,
          offset: offset,
        };
      }

      const response = await apiClient.get<FolderItemsResponse>(
        `/viewer/shared-folder/${folderUuid}/items`,
        {
          params: {
            limit,
            offset,
          },
        }
      );

      // success가 false이고 data가 null인 경우 (백엔드 검증 오류)
      if (!response.data.success && !response.data.data) {
        return {
          items: [],
          total: 0,
          limit: limit,
          offset: offset,
        };
      }

      const data = response.data.data || { items: [], total: 0, limit: limit, offset: offset };
      
      // record가 null인 아이템 필터링 (백엔드 검증 오류 방지)
      const validItems = (data.items || []).filter(item => {
        if (item.record === null || item.record === undefined) {
          return false;
        }
        return true;
      });
      
      const result = {
        items: validItems,
        total: data.total || 0,
        limit: data.limit || limit,
        offset: data.offset || offset,
      };

      // 캐시에 저장
      folderItemsCache.set(cacheKey, {
        data: { items: validItems, total: result.total },
        timestamp: Date.now(),
      });

      return result;
    } catch (error) {
      return {
        items: [],
        total: 0,
        limit: limit,
        offset: offset,
      };
    }
  },

  /**
   * 폴더 내부 annotations 조회 (하위 호환성을 위한 래퍼)
   * @param folderUuid 폴더 UUID
   * @returns 폴더 내부 annotations 목록 (기존 형식 유지)
   * @deprecated getFolderItems를 사용하세요
   */
  getFolderAnnotations: async (folderUuid: string): Promise<any[]> => {
    try {
      const result = await sharedFolderService.getFolderItems(folderUuid);
      
      // 백엔드 검증 오류로 인해 빈 배열이 반환된 경우
      if (result.items.length === 0) {
        // console.warn('[SharedFolder] 폴더 annotations가 비어있습니다. 백엔드 검증 오류일 수 있습니다.');
        return [];
      }
      
      // 기존 FolderAnnotation 형식으로 변환 (하위 호환성)
      // annotation 객체가 있으면 우선 사용, 없으면 record 객체의 정보 활용
      return result.items
        .filter(item => item.record !== null && item.record !== undefined) // record가 있는 아이템만 처리
        .map(item => {
          if (item.item_type === 'ANNOTATION' && item.record) {
            const record = item.record;
            // annotation 객체가 있으면 우선 사용 (더 정확한 데이터)
            if (item.annotation) {
              const annotation = item.annotation;
              return {
                item_no: item.item_no, // 삭제용 item_no 추가
                annotation_uuid: annotation.annotation_uuid,
                book_no: annotation.book_no,
                page_no: annotation.page_no,
                page_idx: annotation.page_idx,
                page_display_number: annotation.page_display_number,
                user_no: annotation.user_no,
                type: annotation.type,
                component_no: annotation.component_no,
                start_offset: annotation.start_offset,
                end_offset: annotation.end_offset,
                data: annotation.data || {},
                is_deleted: annotation.is_deleted,
                version: annotation.version,
                created_at: annotation.created_at,
                updated_at: annotation.updated_at,
                // record에서 추가 정보
                title: record.title,
                summary: record.summary,
                record_uuid: record.record_uuid,
                record_type: record.record_type,
                visibility: record.visibility,
                scope: record.scope,
                anchor_page_no: record.anchor_page_no,
                dialog_no: record.dialog_no,
                dialog_uuid: record.dialog_uuid,
                audio_id: record.audio_id,
                // annotation 객체도 포함 (기존 호환성)
                annotation: item.annotation,
              };
            }
            // annotation 객체가 없으면 record 객체의 정보를 기존 형식으로 변환
            return {
              annotation_uuid: record.annotation_uuid,  // record의 annotation_uuid 사용
              book_no: record.book_no,
              page_no: record.page_no,
              page_idx: record.page_idx,
              page_display_number: record.page_display_number,
              user_no: record.user_no,
              type: record.data?.type || '',
              component_no: record.data?.component_no || null,
              start_offset: record.data?.start_offset || null,
              end_offset: record.data?.end_offset || null,
              data: record.data || {},
              is_deleted: false,
              version: 1,
              created_at: record.created_at,
              updated_at: record.updated_at,
              // 추가 정보
              title: record.title,
              summary: record.summary,
              record_uuid: record.record_uuid,
              record_type: record.record_type,
              visibility: record.visibility,
              scope: record.scope,
              anchor_page_no: record.anchor_page_no,
              dialog_no: record.dialog_no,
              dialog_uuid: record.dialog_uuid,
              audio_id: record.audio_id,
            };
          }
          // ANNOTATION이 아닌 타입 (AUDIO, DIALOG, NOTE 등)
          if (item.record) {
            const record = item.record;
            return {
              item_no: item.item_no,
              record_type: record.record_type,
              record_uuid: record.record_uuid,
              title: record.title,
              summary: record.summary,
              created_at: record.created_at,
              page_no: record.page_no,
              page_idx: record.page_idx,
              page_display_number: record.page_display_number,
              data: record.data || {},
              audio_id: record.audio_id,
              dialog_no: record.dialog_no,
              dialog_uuid: record.dialog_uuid,
              record: item.record,
              annotation: item.annotation,
            };
          }
          return item;
        });
    } catch (error) {
      // console.error('[SharedFolder] 폴더 annotations 조회 오류:', error);
      return [];
    }
  },

  /**
   * 폴더에 아이템 추가
   * @param folderUuid 폴더 UUID
   * @param recordUuid 학습 기록 UUID (record_uuid)
   * @param sortOrder 정렬 순서
   * @param payload 추가 페이로드 데이터
   * @returns success / 실패 시 error 메시지 (HTTP·서버 본문)
   */
  addItemToFolder: async (
    folderUuid: string,
    recordUuid: string,
    sortOrder: number = 0,
    payload: Record<string, any> = {}
  ): Promise<AddItemToFolderResult> => {
    try {
      // console.log('[SharedFolder] 폴더에 아이템 추가 API 호출:', { folderUuid, recordUuid, sortOrder, payload });
      
      const requestData: AddItemToFolderRequest = {
        record_uuid: recordUuid,
        sort_order: sortOrder,
        payload,
      };

      const response = await apiClient.post<AddItemToFolderResponse>(
        `/viewer/shared-folder/${folderUuid}/items`,
        requestData
      );

      // console.log('[SharedFolder] 폴더에 아이템 추가 응답:', response.data);

      if (!response.data.success) {
        const errMsg = response.data.error || '요청이 거절되었습니다.';
        return { success: false, error: errMsg };
      }

      // 캐시 무효화
      sharedFolderService.invalidateCache(undefined, folderUuid);
      return { success: true, error: null };
    } catch (error) {
      return { success: false, error: normalizeAxiosError(error) };
    }
  },

  /**
   * 폴더에 annotation 추가 (하위 호환성을 위한 래퍼)
   * @param folderUuid 폴더 UUID
   * @param recordUuid 학습 기록 UUID (record_uuid)
   * @param sortOrder 정렬 순서
   * @returns 성공 여부
   * @deprecated addItemToFolder를 사용하세요
   */
  addAnnotationToFolder: async (
    folderUuid: string,
    recordUuid: string,
    sortOrder: number = 0
  ): Promise<boolean> => {
    const r = await sharedFolderService.addItemToFolder(
      folderUuid,
      recordUuid,
      sortOrder,
      {}
    );
    return r.success;
  },

  /**
   * 폴더에서 아이템 삭제
   * @param folderUuid 폴더 UUID
   * @param itemNo 아이템 번호 (item_no)
   * @returns 성공 여부
   */
  deleteFolderItem: async (
    folderUuid: string,
    itemNo: number
  ): Promise<boolean> => {
    try {
      // console.log('[SharedFolder] 폴더 아이템 삭제 API 호출:', { folderUuid, itemNo });
      
      const response = await apiClient.delete<DeleteItemResponse>(
        `/viewer/shared-folder/${folderUuid}/items/${itemNo}`
      );

      // console.log('[SharedFolder] 폴더 아이템 삭제 응답:', response.data);

      if (!response.data.success) {
        return false;
      }

      // 캐시 무효화
      sharedFolderService.invalidateCache(undefined, folderUuid);
      return true;
    } catch (error) {
      return false;
    }
  },

  /**
   * 공유 폴더 삭제
   * @param folderUuid 폴더 UUID
   * @returns 성공 여부
   */
  deleteSharedFolder: async (
    folderUuid: string
  ): Promise<boolean> => {
    try {
      // console.log('[SharedFolder] 공유 폴더 삭제 API 호출:', { folderUuid });
      
      const response = await apiClient.delete<DeleteFolderResponse>(
        `/viewer/shared-folder/${folderUuid}`
      );

      // console.log('[SharedFolder] 공유 폴더 삭제 응답:', response.data);

      if (!response.data.success) {
        return false;
      }

      // 전체 캐시 무효화 (폴더 삭제 시 bookUuid를 알 수 없으므로)
      sharedFolderService.invalidateCache();
      return true;
    } catch (error) {
      return false;
    }
  },
};

export default sharedFolderService;
